/* eslint-disable react/no-array-index-key */
import React, {
  ComponentType,
  createRef,
  RefObject,
  useEffect,
  useState,
} from 'react';
import { useCombobox } from 'downshift';
import Input from '@teetsh/app/src/components/Form/Input2';
import { menuItemsStyle } from '@teetsh/app/src/components/Menu/MenuItems';
import { matchSorter } from 'match-sorter';
import tw from 'twin.macro';
import styled from '@emotion/styled';
import {
  MenuItemButton,
  menuItemStyle,
} from '@teetsh/app/src/components/Menu/MenuItem';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import PortalWrapper from '@teetsh/app/src/components/PortalWrapper';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import {
  isReferentialItemCategory,
  ReferentialItemElement,
  SectionReferentialItemElement,
} from '@teetsh/app/src/components/Bo/ReferentialItemElement';
import { getLabelText } from '@teetsh/app/src/components/Bo/referentialLabelParser';

export interface Option {
  value: string;
  label: string;
  ref?: any;
}

export interface Props {
  formGroupClassName?: string;
  helperText?: React.ReactNode;
  options: Option[];
  value: string;
  className?: string;
  id?: string;
  placeholder?: string;
  autoFocus?: boolean;
  label?: string;
  onChange: (v: string) => void;
  error?: string;
  findFct?: (suggestions: Option[], v: string) => Option | undefined;
  touched?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  onBlur?: (v: string) => void;
  noFilterOnSuggestions?: boolean;
  onEnterKeyPress?: () => void;
  Component?: ComponentType<any>;
  portal?: boolean;
  offset?: number;
}

function getItems(items: Option[], filter) {
  return filter
    ? matchSorter(
        items.filter(item => !isReferentialItemCategory(item?.value || '')),
        filter,
        { keys: ['value'] },
      )
    : items;
}

const Menu = styled.div(({ isOpen }: { isOpen: boolean }) => [
  ...menuItemsStyle,
  tw`mt-1 absolute w-full overflow-y-auto max-h-48 bg-white`,
  !isOpen && tw`invisible`,
]);

const MenuItem = styled.div(({ active }: { active: boolean }) => [
  menuItemStyle({ active }),
]);

const RelativeFormGroup = styled(FormGroup)`
  ${tw`relative`}
`;

export function AutoSuggest(props: Props) {
  const {
    inputRef,
    value,
    onChange,
    options,
    label,
    autoFocus,
    onBlur,
    helperText,
    noFilterOnSuggestions,
    placeholder,
    touched,
    error,
    findFct,
    portal = false,
    offset: offsetValue = 2,
    Component = Input,
  } = props;
  const [inputItems, setInputItems] = useState<Option[]>(
    props.options.map(opt => ({
      ...opt,
      ref: createRef(),
    })),
  );
  const { refs, floatingStyles } = useFloating({
    middleware: [
      offset(offsetValue),
      flip(),
      shift(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    getLabelProps,
    openMenu,
    selectItem,
  } = useCombobox({
    inputValue: value,
    items: inputItems,
    defaultIsOpen: autoFocus,
    initialIsOpen: autoFocus,
    stateReducer: (_, actionAndChanges) => {
      const { changes } = actionAndChanges;
      if (changes?.isOpen && findFct) {
        const matchingValue = findFct(options, value)?.value;
        if (matchingValue) {
          const matchingIndex = options
            .map(opt => opt.value)
            .indexOf(matchingValue);
          return {
            ...changes,
            highlightedIndex: matchingIndex,
          };
        }
      }
      return changes;
    },

    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      // ça c'est de la merde
      // dans AddCompetencesButton ça newSelectedItem est un string
      // dans AutoSuggest c'est un object
      if (typeof newSelectedItem === 'string') {
        onChange(getLabelText(newSelectedItem));
        if (onBlur) {
          onBlur(getLabelText(newSelectedItem));
        }
        return;
      }
      if (!newSelectedItem) {
        return;
      }
      onChange(getLabelText(newSelectedItem.value));
      if (onBlur) {
        onBlur(getLabelText(newSelectedItem.value));
      }
      return;
    },
    onInputValueChange: ({ inputValue }) => {
      if (!noFilterOnSuggestions) {
        setInputItems(getItems(options, inputValue));
      }
      if (inputValue && inputValue !== '[object Object]') {
        onChange(inputValue);
      }
    },
  });

  useEffect(() => {
    setInputItems(
      props.options.map(opt => ({
        ...opt,
        ref: createRef(),
      })),
    );
  }, [props.options.map(opt => `${opt.value} ${opt.label}`).join('')]);

  useEffect(() => {
    if (findFct && isOpen) {
      const matchingSuggestion = findFct(inputItems, value);
      if (matchingSuggestion?.ref?.current) {
        matchingSuggestion.ref.current.scrollIntoView({
          block: 'center',
        });
      }
    }
  }, [isOpen]);

  return (
    <RelativeFormGroup
      withoutMargin
      error={touched ? error : undefined}
      label={label}
      helperText={helperText}
      {...getLabelProps()}
    >
      <div ref={portal ? (refs.setReference as any) : null}>
        <Component
          // autoComplete="off"
          placeholder={placeholder}
          {...getInputProps({
            refKey: 'inputRef',
            // C'est dans le cas où l'input c'est un textarea, ça evite que le menu s'affiche dans le textarea
            ref: inputRef,
            // Dans le cas du multitag le simple focus ne marche pas pour ouvrir le popover
            // on utilise donc le click et le focus pour ouvrir le popover pour éviter d'avoir à distinguer
            // les 2 types
            onClick: () => {
              if (!isOpen) {
                openMenu();
              }
            },
            onFocus: () => {
              if (!isOpen) {
                openMenu();
              }
            },
            // https://github.com/downshift-js/downshift/issues/1108#issuecomment-674180157
            onChange: e => {
              onChange(e.currentTarget.value);
            },
            onBlur: () => {
              if (onBlur) {
                onBlur(value);
              }
            },
          })}
        />
      </div>
      <div {...getMenuProps()}>
        <div>
          {isOpen && (inputItems || []).length > 0 && (
            <PortalWrapper portal={portal}>
              <Menu
                isOpen
                ref={refs.setFloating as any}
                style={
                  portal
                    ? {
                        ...floatingStyles,
                      }
                    : {}
                }
              >
                {inputItems.map((item, index) => {
                  const active = index === highlightedIndex;
                  if (isReferentialItemCategory(item?.label || '')) {
                    return (
                      <SectionReferentialItemElement
                        key={`${item}${index}`}
                        label={item.label}
                      />
                    );
                  }

                  return (
                    <MenuItem
                      key={`${item}${index}`}
                      active={active}
                      {...getItemProps({ item, index })}
                      onMouseDown={() => {
                        // https://github.com/downshift-js/downshift/issues/287
                        if (portal) {
                          selectItem(item.value as any);
                        }
                      }}
                      tw="even:bg-neutral-supershy"
                    >
                      <MenuItemButton
                        buttonRef={item.ref}
                        text={<ReferentialItemElement label={item.label} />}
                      />
                    </MenuItem>
                  );
                })}
              </Menu>
            </PortalWrapper>
          )}
        </div>
      </div>
    </RelativeFormGroup>
  );
}
